import {tokens, colors} from '@commercial-helios/ui/theme'
import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import type {BigListSliceFragment} from '@/graphql-types'
import numberOneTrophyIcon from '@/images/icons/number-one-trophy.svg'
import smileyIcon from '@/images/icons/smiley.svg'
import starIcon from '@/images/icons/star-drawn.svg'
import PrismicLink from '@helios/shared/components/prismic-link'
import {BackgroundWrapper, Section} from '@helios/shared/styles/grid'
import {Text, TextLink} from '@helios/shared/styles/typography'
import mq from '@helios/shared/utils/media-query'

const tabletBreakpoint = 'sm'

export enum BgColor {
    Gray = 'gray',
    White = 'white',
    Pink = 'pink',
    Yellow = 'yellow',
}

export enum Icon {
    NoIcon = 'none',
    Smiley = 'smiley',
    Star = 'star',
    Trophy = 'trophy',
}

const mapIcon = (key: string): string => {
    switch (key) {
        case Icon.Smiley:
            return smileyIcon
        case Icon.Star:
            return starIcon
        default:
        case Icon.Trophy:
            return numberOneTrophyIcon
    }
}

const mapBackgroundColor = (key: string) => {
    switch (key) {
        default:
        case BgColor.Gray:
            return 'muted'
        case BgColor.Pink:
            return 'pink'
        case BgColor.White:
            return 'default'
        case BgColor.Yellow:
            return 'yellow'
    }
}

interface Props {
    slice: BigListSliceFragment
}

export const BigListSection = ({slice}: Props) => {
    return (
        <BackgroundWrapper color={mapBackgroundColor(slice.primary.background_color)}>
            <ContentWrapper>
                {slice.primary?.description && (
                    <Text variant="title-base">{slice.primary?.description}</Text>
                )}
                <List>
                    {slice.items.map((item, i) => (
                        <ListItem key={i}>
                            {item.icon && item.icon !== Icon.NoIcon && (
                                <ListItemIcon alt="" src={mapIcon(item.icon)} />
                            )}
                            <PrimaryLabel variant="heading-base">{item.label}</PrimaryLabel>

                            {item.secondary_label && (
                                <SecondaryLabel>
                                    {item.link?.url ? (
                                        <TextLink
                                            as={PrismicLink}
                                            {...item.link}
                                            variant="heading-base"
                                        >
                                            {item.secondary_label}
                                        </TextLink>
                                    ) : (
                                        <Text variant="heading-base">{item.secondary_label}</Text>
                                    )}
                                </SecondaryLabel>
                            )}
                        </ListItem>
                    ))}
                </List>
            </ContentWrapper>
        </BackgroundWrapper>
    )
}

export default BigListSection

export const ContentWrapper = styled(Section)`
    display: grid;
    margin-top: 0;
    row-gap: ${tokens.spacing60};
`

export const List = styled.ul`
    display: grid;
    padding: 0;
    margin: 0;
    row-gap: ${tokens.spacing20};
`

export const ListItem = styled.li`
    display: flex;
    padding: 0 0 ${tokens.spacing8};
    flex-direction: column;
    border-bottom: 1px solid ${colors.border.secondary};

    ${mq.min(tabletBreakpoint)} {
        padding: 0 0 ${tokens.spacing20};
        flex-direction: row;
    }
`

const ListItemIcon = styled.img`
    display: none;

    ${mq.min(tabletBreakpoint)} {
        display: inline-block;
        width: 40px;
        height: 40px;
        margin: ${tokens.spacing6} ${tokens.spacing20} 0 0;
    }
`

const PrimaryLabel = styled(Text)`
    display: block;
    flex-grow: 1;
    min-width: 50%;
`

const SecondaryLabel = styled.span`
    min-width: 25%;
    margin-top: ${tokens.spacing8};
    text-align: right;

    a {
        text-decoration: none;
        border-bottom: 2px solid ${colors.border.secondary};

        &:hover {
            background: transparent;
        }
    }

    ${mq.min(tabletBreakpoint)} {
        margin-top: 0;
    }
`

export const bigListSlice = graphql`
    fragment BigListSlice on PrismicWhyPleoPageDataBodyBigList {
        __typename
        primary {
            background_color
            description
        }
        items {
            icon
            label
            secondary_label
            link {
                ...PrismicLink
            }
        }
    }
`
